import React, { useState, useEffect } from "react";
import {
  Copy,
  Share,
  MessageCircle,
  Twitter,
  Facebook,
  Mail,
  Check,
  Users,
  Coins,
  Clock,
} from "lucide-react";
import referralService from "../../../services/api/referral.service";
import { toast } from "react-toastify";

const ReferralCodeCard = ({ activeRewardAmount, minimumTransactionAmount }) => {
  const [referralCode, setReferralCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Configure your referral message and benefits here
  const referralInfo = {
    companyName: "Investhive", // Replace with your platform name
    bonusAmount: activeRewardAmount ? `₹${activeRewardAmount.toLocaleString("en-IN")}` : "exclusive rewards",
    discountPercentage: "15%", // Replace with actual discount if applicable
    mainBenefit: activeRewardAmount ? `earn ₹${activeRewardAmount.toLocaleString("en-IN")} on every successful referral` : "access to premium investment opportunities",
    secondaryBenefit: "lower fees",
  };

  // Fetch referral code automatically on component mount
  useEffect(() => {
    const fetchReferralCode = async () => {
      try {
        setIsLoading(true);
        const response = await referralService.generateReferralCode();
        if (response.success) {
          setReferralCode(response.data.referralCode);
          // toast.success('Referral code generated successfully!');
        }
      } catch (error) {
        console.error("Error generating referral code:", error);
        toast.error("Failed to generate referral code. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralCode();
  }, []);

  // Function to handle copy
  const handleCopyCode = () => {
    if (!referralCode) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(referralCode)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => toast.error("Failed to copy referral code."));
    } else {
      // Fallback for unsupported browsers
      const tempInput = document.createElement("textarea");
      tempInput.value = referralCode;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareLink = () => {
    if (!referralCode) return;

    const referralLink = `${window.location.origin}/referral?ref=${referralCode}`;
    const fullMessage = `Professional Networking Opportunity: Join common investment circles with ${referralInfo.companyName}.\n\nI'm using ${referralInfo.companyName} to diversify my portfolio with premium unlisted stocks. Use my exclusive referral code ${referralCode} to unlock ${referralInfo.secondaryBenefit} and priority access.\n\nSecure your access here: ${referralLink}`;

    if (navigator.share) {
      navigator.share({
        title: `Join ${referralInfo.companyName}`,
        text: fullMessage,
      }).catch(console.error);
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(fullMessage)
        .then(() => toast.success("Share text and link copied!"))
        .catch(() => toast.error("Failed to copy share text."));
    } else {
      const tempInput = document.createElement("textarea");
      tempInput.value = fullMessage;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      toast.success("Share text and link copied!");
    }
  };

  // Generate professional referral messages
  const getReferralMessage = (platform) => {
    const referralLink = `${window.location.origin}/referral?ref=${referralCode}`;
    const baseMessage = `I'm inviting you to join ${referralInfo.companyName}, a premier platform for unlisted equity investments.`;

    // Professional, neutral messaging for senior network
    switch (platform) {
      case "whatsapp":
        return `*Professional Investment Invitation* 📈\n\n${baseMessage}\n\nReferral Code: *${referralCode}*\nBenefits: ${referralInfo.secondaryBenefit} & Preferred Member Terms.\n\nAccess here: ${referralLink}`;

      case "twitter":
        return `Exploring high-potential unlisted opportunities with @${referralInfo.companyName}. 📈 Join my private network using code ${referralCode} for exclusive access & ${referralInfo.secondaryBenefit}. #Investing: ${referralLink}`;

      case "facebook":
        return `I've discovered a institutional-grade approach to private equity and unlisted stocks. Join my circle on ${referralInfo.companyName} using code ${referralCode}. We both receive ${referralInfo.secondaryBenefit} and preferred transaction terms. Start building your portfolio: ${referralLink}`;

      case "email":
        return `Subject: Invitation to Join my Private Investment Circle on ${referralInfo.companyName}\n\n
Hello,

I've been utilizing ${referralInfo.companyName} to diversify my portfolio with institutional-grade unlisted stocks and thought this would align with your high-growth investment goals.

By joining my professional network, you'll receive exclusive member benefits:
- ${referralInfo.secondaryBenefit}
- Preferred transaction terms and priority allocation
- Early access to premium, high-potential opportunities

Your Referral Code: ${referralCode}
Secure Digital Onboarding: ${referralLink}

Best regards,`;

      default:
        return `Join my private investment circle on ${referralInfo.companyName}. Use code: ${referralCode} for exclusive benefits and preferred access. ${referralLink}`;
    }
  };

  const shareReferral = (platform) => {
    const message = getReferralMessage(platform);
    let shareUrl = "";

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          message
        )}`;
        break;
      case "facebook":
        // For Facebook, we'll use the link with a pre-filled message
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          `${window.location.origin}/referral?ref=${referralCode}`
        )}&quote=${encodeURIComponent(message)}`;
        break;
      case "email":
        const emailParts = message.split("Subject: ");
        const emailSubject = emailParts[1].split("\n\n")[0];
        const emailBody = emailParts[1].split("\n\n")[1];
        shareUrl = `mailto:?subject=${encodeURIComponent(
          emailSubject
        )}&body=${encodeURIComponent(emailBody)}`;
        break;
      default:
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 transition-transform duration-300">
      <div className="flex flex-col md:flex-row">
        {/* Left side - Text content */}
        <div className="w-full md:w-1/2 bg-indigo-950 text-white p-8 flex flex-col justify-center relative border-r border-indigo-900/50">
          {activeRewardAmount > 0 && (
            <div className="absolute top-6 right-6 bg-cyan-400 text-indigo-950 px-4 py-1.5 rounded-full font-bold text-sm shadow-lg flex items-center animate-pulse">
              <Coins size={16} className="mr-2" />
              EARN ₹{activeRewardAmount.toLocaleString("en-IN")}
            </div>
          )}
          <h2 className="text-3xl font-bold mb-6"> Refer & Earn </h2>

          <p className="text-lg mb-8 text-gray-300">
            Share our investment platform with friends and watch your rewards
            grow with every investment they make.
          </p>

          <div className="space-y-6">
            <div className="flex items-start">
              <div className="bg-cyan-400 text-indigo-950 rounded-full p-2 mr-4">
                <Users size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Unlimited Referrals</h3>
                <p className="text-gray-400">
                  Invite as many friends as you want
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-cyan-400 text-indigo-950 rounded-full p-2 mr-4">
                <Coins size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Compound Rewards</h3>
                <p className="text-gray-400">
                  Earn on every investment they make
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-cyan-400 text-indigo-950 rounded-full p-2 mr-4">
                <Clock size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Long-term Benefits</h3>
                <p className="text-gray-400">Rewards continue for 6 months</p>
              </div>
            </div>

            {minimumTransactionAmount > 0 && (
              <div className="mt-4 flex items-center bg-indigo-900 border border-indigo-700 rounded-lg px-4 py-3">
                <Coins size={18} className="text-cyan-400 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Min. Investment Required</p>
                  <p className="text-white font-bold text-lg">
                    ₹{minimumTransactionAmount.toLocaleString("en-IN")}
                  </p>
                  <p className="text-gray-400 text-xs mt-0.5">Your referee must invest this amount to qualify</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Referral code display */}
        <div className="w-full md:w-1/2 p-8 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Referral Program
            </h3>
            <p className="text-gray-500 mb-6 font-medium">
              Recommend us to your network and {activeRewardAmount ? `earn ₹${activeRewardAmount.toLocaleString("en-IN")}` : "get rewarded"}
            </p>

            <div className="mb-8">
              <div className="p-6 border border-gray-200 rounded-xl bg-gray-50 mb-4">
                <div className="text-center mb-3">
                  <p className="text-sm text-gray-500">
                    Your Unique Referral Code
                  </p>
                </div>
                <div className="flex items-center bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <div className="flex-1 px-4 py-3 font-mono text-xl font-medium text-gray-800 text-center">
                    {referralCode ||
                      (isLoading ? "Loading..." : "No code available")}
                  </div>
                  <button
                    onClick={handleCopyCode}
                    title="Copy Code"
                    className={`${copied ? "bg-cyan-400" : "bg-indigo-950"
                      } text-white px-4 py-3 flex items-center transition-colors`}
                  >
                    {copied ? (
                      <>
                        <Check size={18} className="mr-2" />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={18} className="mr-2" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleShareLink}
                    title="Share Link"
                    className="bg-indigo-900 border-l border-indigo-700 text-white px-4 py-3 flex items-center transition-colors hover:bg-indigo-800"
                  >
                    <Share size={18} className="mr-2" />
                    <span>Share</span>
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-4 text-center">
                Share this code with friends to start earning rewards
              </p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => shareReferral("whatsapp")}
                  className="bg-cyan-400 hover:bg-cyan-500 text-indigo-950 py-3 px-4 rounded-lg flex items-center justify-center"
                >
                  <MessageCircle size={18} className="mr-2" />
                  <span>WhatsApp</span>
                </button>
                <button
                  onClick={() => shareReferral("twitter")}
                  className="bg-cyan-400 hover:bg-cyan-500 text-indigo-950 py-3 px-4 rounded-lg flex items-center justify-center"
                >
                  <Twitter size={18} className="mr-2" />
                  <span>Twitter</span>
                </button>
                <button
                  onClick={() => shareReferral("facebook")}
                  className="bg-cyan-400 hover:bg-cyan-500 text-indigo-950 py-3 px-4 rounded-lg flex items-center justify-center"
                >
                  <Facebook size={18} className="mr-2" />
                  <span>Facebook</span>
                </button>
                <button
                  onClick={() => shareReferral("email")}
                  className="bg-cyan-400 hover:bg-cyan-500 text-indigo-950 py-3 px-4 rounded-lg flex items-center justify-center"
                >
                  <Mail size={18} className="mr-2" />
                  <span>Email</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralCodeCard;
